import { Button, List, Skeleton, Space, Switch, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { describeHint } from '@/Ship/Configs/HintCatalog'
import { tutorialManager } from '@/Ship/Stores/TutorialManager'

const { Text } = Typography

/** Правильна форма слова «підказка» після числа. */
const hintsWord = (count: number): string => {
    const tail = count % 100

    if (tail >= 11 && tail <= 14) return 'підказок'

    switch (count % 10) {
        case 1:
            return 'підказку'
        case 2:
        case 3:
        case 4:
            return 'підказки'
        default:
            return 'підказок'
    }
}

/**
 * Налаштування інтерфейсу профілю. Поки одне — режим підказок.
 *
 * ⚠️ Живе саме тут, у `ProfileContainer`, і це не байдуже: у еталоні ядра
 * профілів було **два** — `Setting.Profile` (шапка) і `Setting.UserProfile`
 * (лише пряма адреса), і перша версія цієї вкладки опинилася на другому, де
 * замовник її не знайшов. Тут другий профіль прибрано (`SPEC-005` §3.7).
 *
 * Опис без «і звітах»: звітів на етапі 1 немає (`SPEC-005` §7.3).
 *
 * ⚠️ Стан зберігається на **акаунті** (опція користувача `tutorial_mode`), а не
 * в браузері: людина, яка вимкнула підказки на робочому місці, не має вмикати
 * їх наново з ноутбука.
 *
 * ⚠️ «Показати знову» — обов'язкова пара до хрестика на самій підказці. Без неї
 * прибрана поштучно підказка зникає безповоротно, і повернути її можна хіба що
 * через базу — а користувач про це не знає й вирішить, що фіча зламалася.
 *
 * Прибрані підказки розгортаються списком — де кожна стоїть (`HintCatalog`), і
 * повернути можна одну, не скидаючи решту (рішення замовника 2026-10-23).
 */
export const Interface = observer(() => {
    const [isLoading, setIsLoading] = useState(true)
    const [isRestoring, setIsRestoring] = useState(false)
    const [restoringId, setRestoringId] = useState<string | null>(null)
    const [isListOpen, setIsListOpen] = useState(false)

    useEffect(() => {
        void tutorialManager.load().finally(() => setIsLoading(false))
    }, [])

    if (isLoading) return <Skeleton active={true} />

    const hiddenIds = tutorialManager.hidden
    const hidden = hiddenIds.length
    const isBusy = isRestoring || restoringId !== null

    // Розділ, потім місце — як вони йдуть у меню; сортуємо, а не за порядком прибирання
    const hiddenRows = hiddenIds
        .map((id) => ({ id, ...describeHint(id) }))
        .sort((a, b) => a.section.localeCompare(b.section, 'uk') || a.place.localeCompare(b.place, 'uk'))

    const rows = [
        {
            key: 'tutorial',
            title: 'Режим підказок',
            description: 'Пояснення в картках.',
            control: (
                <Switch
                    checked={tutorialManager.isEnabled}
                    onChange={(value) => void tutorialManager.setEnabled(value)}
                />
            ),
        },
        {
            key: 'restore',
            title: 'Прибрані підказки',
            description: hidden > 0 ? `Прибрано ${hidden} ${hintsWord(hidden)}.` : 'Ви ще жодної не прибрали.',
            control: (
                <Space size={8}>
                    <Button size="small" disabled={hidden === 0} onClick={() => setIsListOpen((open) => !open)}>
                        {isListOpen && hidden > 0 ? 'Сховати список' : 'Переглянути'}
                    </Button>
                    <Button
                        size="small"
                        disabled={hidden === 0 || isBusy}
                        loading={isRestoring}
                        onClick={() => {
                            setIsRestoring(true)
                            void tutorialManager.restoreAll().finally(() => setIsRestoring(false))
                        }}
                    >
                        Показати всі
                    </Button>
                </Space>
            ),
        },
    ]

    return (
        <>
            <List
                dataSource={rows}
                renderItem={(row) => (
                    <List.Item key={row.key} actions={[row.control]}>
                        <List.Item.Meta
                            title={row.title}
                            description={<Text type="secondary">{row.description}</Text>}
                        />
                    </List.Item>
                )}
            />
            {isListOpen && hidden > 0 ? (
                <List
                    size="small"
                    bordered
                    data-testid="hidden-hints"
                    style={{ marginTop: 8 }}
                    dataSource={hiddenRows}
                    renderItem={(row) => (
                        <List.Item
                            key={row.id}
                            actions={[
                                <Button
                                    key="restore"
                                    size="small"
                                    type="link"
                                    disabled={isBusy && restoringId !== row.id}
                                    loading={restoringId === row.id}
                                    onClick={() => {
                                        setRestoringId(row.id)
                                        void tutorialManager.restore(row.id).finally(() => setRestoringId(null))
                                    }}
                                >
                                    Показати знову
                                </Button>,
                            ]}
                        >
                            <List.Item.Meta
                                title={row.place}
                                description={<Text type="secondary">{row.section}</Text>}
                            />
                        </List.Item>
                    )}
                />
            ) : null}
        </>
    )
})
